import React, { useState } from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';

const formatDate = (date) =>
  date ? dayjs(date).format('D MMM YYYY, HH:mm') : '—';

function DocumentDownloadsTable({ documents }) {
  const [expandedId, setExpandedId] = useState(null);

  if (!documents || documents.length === 0) {
    return (
      <EmptyState>
        <EmptyMessage>No documents uploaded yet</EmptyMessage>
      </EmptyState>
    );
  }

  const toggleRow = (documentId) =>
    setExpandedId((current) => (current === documentId ? null : documentId));

  return (
    <TableContainer>
      <Table>
        <TableHeader>
          <HeaderRow>
            <HeaderCell>Document</HeaderCell>
            <HeaderCell>Downloads</HeaderCell>
            <HeaderCell>Unique Users</HeaderCell>
            <HeaderCell>Last Downloaded</HeaderCell>
          </HeaderRow>
        </TableHeader>
        <TableBody>
          {documents.map((document, index) => {
            const isExpandable = document.users && document.users.length > 0;
            const isExpanded = expandedId === document.documentId;
            return (
              <React.Fragment key={document.documentId}>
                <BodyRow
                  $isEven={index % 2 === 0}
                  $isExpandable={isExpandable}
                  onClick={
                    isExpandable
                      ? () => toggleRow(document.documentId)
                      : undefined
                  }>
                  <BodyCell>
                    <DocumentName>
                      <Chevron
                        $isExpanded={isExpanded}
                        $isHidden={!isExpandable}
                      />
                      <span>
                        {document.fileName}
                        {document.deleted && (
                          <DeletedTag> (deleted)</DeletedTag>
                        )}
                      </span>
                    </DocumentName>
                  </BodyCell>
                  <BodyCell>
                    <MetricValue>{document.downloads}</MetricValue>
                  </BodyCell>
                  <BodyCell>
                    <MetricValue>{document.uniqueUsers}</MetricValue>
                  </BodyCell>
                  <BodyCell>
                    <MetricValue>
                      {formatDate(document.lastDownloadedAt)}
                    </MetricValue>
                  </BodyCell>
                </BodyRow>
                {isExpandable &&
                  isExpanded &&
                  document.users.map((user) => (
                    <UserRow key={user.uid}>
                      <UserCell>
                        <UserName>{user.name}</UserName>
                      </UserCell>
                      <UserCell>
                        <UserMetric>{user.downloads}</UserMetric>
                      </UserCell>
                      <UserCell />
                      <UserCell>
                        <UserMetric>
                          {formatDate(user.lastDownloadedAt)}
                        </UserMetric>
                      </UserCell>
                    </UserRow>
                  ))}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const TableContainer = styled.div`
  overflow-x: auto;
  padding: 0 1.5rem 1.5rem 1.5rem;
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
`;

const TableHeader = styled.thead``;

const TableBody = styled.tbody``;

const HeaderRow = styled.tr``;

const BodyRow = styled.tr`
  background-color: ${({ $isEven }) => ($isEven ? '#f9f9f9' : 'white')};
  cursor: ${({ $isExpandable }) => ($isExpandable ? 'pointer' : 'default')};

  &:hover {
    background-color: var(--light-grey);
  }
`;

const UserRow = styled.tr`
  background-color: #f4f7ff;
`;

const HeaderCell = styled.th`
  border-bottom: 2px solid var(--light-grey);
  color: var(--brown);
  font-family: 'Poppins', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 0.75rem 1rem;
  text-align: left;
`;

const BodyCell = styled.td`
  padding: 1rem;
  vertical-align: top;
`;

const UserCell = styled.td`
  padding: 0.5rem 1rem;
  vertical-align: top;
`;

const Chevron = styled.span`
  border-bottom: 2px solid var(--light-brown);
  border-right: 2px solid var(--light-brown);
  flex-shrink: 0;
  height: 0.4rem;
  /* Nudge left so the rotated glyph's visual mass, which sits right of the
     box centre, lines up with the column edge */
  margin-left: -0.1rem;
  transform: ${({ $isExpanded }) =>
    $isExpanded ? 'rotate(45deg)' : 'rotate(-45deg)'};
  transition: transform 0.15s ease;
  /* Kept in the layout when there is nothing to expand, so every filename in
     the column starts at the same x */
  visibility: ${({ $isHidden }) => ($isHidden ? 'hidden' : 'visible')};
  width: 0.4rem;
`;

const DocumentName = styled.div`
  align-items: center;
  color: var(--brown);
  display: flex;
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  font-weight: 500;
  gap: 0.5rem;
  overflow-wrap: anywhere;
`;

const DeletedTag = styled.span`
  color: var(--light-brown);
  font-weight: 400;
`;

const MetricValue = styled.div`
  color: var(--brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  font-weight: 500;
`;

const UserName = styled.div`
  color: var(--light-brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
  padding-left: 1.4rem;
`;

const UserMetric = styled.div`
  color: var(--light-brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
`;

const EmptyState = styled.div`
  padding: 2rem;
  text-align: center;
`;

const EmptyMessage = styled.p`
  color: var(--light-brown);
  font-family: 'Inter', sans-serif;
  margin: 0;
`;

export default DocumentDownloadsTable;
